/**
 * Окно «История» товара договора (кнопка у товара во вкладке «Договор»): вкладки «Выдача» — кто и
 * когда выдал / вернул (`sMove_deal`, кнопки «Выдать» / «Вернуть» CRM), откуда → куда и штраф при
 * возврате; «Задачи» — задачи перемещения серии в сделке (`sMove_task`: статус, кто, куда, действия, сумма
 * биллинга); «Биллинг» — премия строки и проводки задач серии (как «Задачи» и «Премия» истории товара
 * CRM); «Цена» — установка и смены цены; «Правки» — остальные правки строки договора.
 * Только чтение CRM. Спека `docs/specs/2026-10-23-contract-issue-history-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { type ProductHistory, productHistoryQuery } from "@/lib/dealContract";

type Tab = "issue" | "tasks" | "billing" | "price" | "edits";

const at = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(2, 4)} ${iso.slice(11, 16)}` : "—");
const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;
const tabBtn = (on: boolean) =>
  `rounded-full px-2.5 py-0.5 text-[12.5px] ${on ? "bg-accent text-white" : "border border-border text-fg hover:bg-bg-subtle"}`;
// Цвета операций — как `sMove_deal_operation.textColor` CRM: выдача оранжевая, возврат зелёный.
const OP_COLOR: Record<number, string> = { 1: "text-warn", 2: "text-ok", 5: "text-err" };

function Row({ when, who, children }: { when: string | null; who: string | null; children: React.ReactNode }) {
  return (
    <li className="flex flex-col gap-0.5 border-t border-border py-1.5 first:border-t-0">
      <div className="text-[11.5px] text-fg-muted tabular-nums">
        {at(when)}
        {who ? ` · ${who}` : ""}
      </div>
      <div>{children}</div>
    </li>
  );
}

const empty = (text: string) => <div className="py-3 text-fg-muted">{text}</div>;

function Issue({ h }: { h: ProductHistory }) {
  const [repeats, setRepeats] = useState(false);
  const moves = repeats ? h.moves : h.moves.filter((m) => !m.repeat);
  const hidden = h.moves.length - h.moves.filter((m) => !m.repeat).length;
  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-2.5" data-annot="состояние выдачи">
        {h.issue === "none" ? (
          <span className="font-medium">Не выдан</span>
        ) : (
          <>
            <span className={`font-medium ${h.issue === "returned" ? "text-ok" : h.state === "не возврат" ? "text-err" : "text-warn"}`}>
              {h.state === "выдан" ? "Выдан" : h.state === "возвращён" ? "Возвращён" : "Не возврат"}
            </span>
            <span className="text-fg-muted">
              {" "}
              · {at(h.moved?.at ?? null)}
              {h.moved?.who ? ` · ${h.moved.who}` : ""}
            </span>
          </>
        )}
        {h.is_sale && <span className="text-fg-muted"> · продажа, назад не ждём</span>}
      </div>
      {moves.length === 0 ? (
        empty("Товар не выдавали.")
      ) : (
        <ul data-annot="операции выдачи">
          {moves.map((m) => (
            <Row key={m.id} when={m.at} who={m.who}>
              <span className={`font-medium ${OP_COLOR[m.op] ?? ""}`}>{m.name}</span>
              {m.repeat && <span className="text-fg-faint"> · повторный скан</span>}
              {(m.from || m.to) && (
                <div className="text-[12px] text-fg-muted">
                  {m.from ?? "—"} → {m.to ?? "—"}
                </div>
              )}
            </Row>
          ))}
        </ul>
      )}
      {hidden > 0 && (
        <button type="button" onClick={() => setRepeats((v) => !v)} className="self-start text-[12px] text-accent-text hover:underline">
          {repeats ? "скрыть повторные сканы" : `показать повторные сканы (${hidden})`}
        </button>
      )}
      {h.penalties.length > 0 && (
        <>
          <div className="mt-1 text-[12px] font-medium text-fg-muted">Штраф при возврате</div>
          <ul data-annot="штрафы">
            {h.penalties.map((w, i) => (
              <Row key={i} when={w.at} who={w.who}>
                {w.status ?? "—"}
                {w.sum ? <span className="font-medium tabular-nums"> · {rub(w.sum)}</span> : null}
              </Row>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Price({ h }: { h: ProductHistory }) {
  if (h.price.length === 0) return empty("Цену не меняли.");
  return (
    <ul data-annot="история цены">
      {h.price.map((p, i) => (
        <Row key={i} when={p.at} who={p.who}>
          {p.old != null && <span className="text-fg-muted line-through tabular-nums">{rub(p.old)}</span>}
          {p.old != null && " → "}
          <span className="font-medium tabular-nums">{rub(p.new)}</span>
          {p.type && <span className="text-fg-muted"> · {p.type}</span>}
        </Row>
      ))}
    </ul>
  );
}

const signed = (n: number) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toLocaleString("ru-RU")} ₽`;
const amountCls = (n: number) => (n > 0 ? "text-ok" : n < 0 ? "text-err" : "text-fg-muted");
const userOf = (u: string | null, loc: string | null) => (u ? `${u}${loc ? ` · ${loc}` : ""}` : null);

/** Задачи перемещения серии в сделке — как вкладка «Задачи» истории товара CRM: группа, статус, кто,
 *  куда переместили, сумма биллинга задачи и действия по ней. */
function Tasks({ h }: { h: ProductHistory }) {
  if (h.tasks.length === 0) return empty("Задач по товару в этой сделке нет.");
  return (
    <ul className="flex flex-col gap-1.5" data-annot="задачи товара">
      {h.tasks.map((t) => (
        <li key={t.id} className="flex flex-col gap-0.5 rounded-[var(--radius-card)] border border-border p-2">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium">{t.group ?? t.type ?? "Задача"}</span>
            {t.type && t.type !== t.group && <span className="text-fg-muted">{t.type}</span>}
            <span className={`text-[12px] ${t.cancel ? "text-fg-faint line-through" : t.done ? "text-ok" : "text-warn"}`}>{t.status ?? "—"}</span>
            {t.billing.length > 0 && <span className={`ml-auto font-medium tabular-nums ${amountCls(t.net)}`}>{signed(t.net)}</span>}
          </div>
          <div className="text-[11.5px] text-fg-muted tabular-nums">
            создана {at(t.created)}
            {t.done_at ? ` · выполнена ${at(t.done_at)}` : ""}
            {t.user ? ` · ${userOf(t.user, t.user_loc)}` : ""}
          </div>
          {t.place_to && (
            <div className="text-[12px] text-fg-muted">
              → {t.place_to}
              {t.place_to_loc && !t.place_to.startsWith(t.place_to_loc) ? ` · ${t.place_to_loc}` : ""}
            </div>
          )}
          {t.comment?.trim() && <div className="text-[12px]">«{t.comment.trim()}»</div>}
          {t.mismatch && <div className="text-[11.5px] text-warn">сумма задачи в CRM {t.billing_sum ?? 0} ₽ ≠ проводки {t.net} ₽</div>}
          {t.actions.length > 0 && (
            <ul className="mt-0.5 flex flex-col gap-0.5 border-l-2 border-border pl-2 text-[11.5px]">
              {t.actions.map((a, i) => (
                <li key={i}>
                  <span className="text-fg-muted tabular-nums">{at(a.at)}</span> {a.who ?? "—"} · <span className="font-medium">{a.name}</span>
                  {a.to ? ` → ${a.to}` : ""}
                  {a.delay ? ` · до ${at(a.delay)}` : ""}
                  {a.comment ? ` · «${a.comment}»` : ""}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Биллинг товара: премия строки договора и проводки задач серии — как «Премия» истории товара CRM. */
function Billing({ h }: { h: ProductHistory }) {
  const b = h.billing;
  if (!b || b.rows.length === 0) return empty("Начислений по товару нет.");
  const t = b.total;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-2.5 tabular-nums" data-annot="итог биллинга товара">
        <span>
          Итого <span className={`font-medium ${amountCls(t.net)}`}>{signed(t.net)}</span>
        </span>
        <span className="text-fg-muted">начислено {signed(t.accrued)}</span>
        {t.written_off !== 0 && <span className="text-fg-muted">списано {signed(t.written_off)}</span>}
        <span className="text-fg-muted">сотрудников {t.users}</span>
      </div>
      <ul data-annot="проводки товара">
        {b.rows.map((r) => (
          <Row key={r.id} when={r.at} who={userOf(r.user, r.user_loc)}>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium">{r.module}</span>
              {r.category !== r.module && <span className="text-fg-muted">{r.category}</span>}
              <span className={`ml-auto font-medium tabular-nums ${amountCls(r.amount)}`}>{signed(r.amount)}</span>
            </div>
            <div className="text-[11.5px] text-fg-muted">
              {r.correct_of ? `корректировка проводки ${r.correct_of}` : r.task_id ? `задача ${r.task_id}` : r.bonus_deal_id ? "премия по сделке" : `проводка ${r.id}`}
              {r.comment ? ` · «${r.comment}»` : ""}
            </div>
          </Row>
        ))}
      </ul>
    </div>
  );
}

function Edits({ h }: { h: ProductHistory }) {
  if (h.edits.length === 0) return empty("Правок нет.");
  return (
    <ul data-annot="правки товара">
      {h.edits.map((e, i) => (
        <Row key={i} when={e.at} who={e.who}>
          <span className="font-medium">{e.field}</span>
          {(e.old != null || e.new != null) && (
            <span className="break-words">
              : {e.old != null && <span className="text-fg-muted line-through">{e.old}</span>}
              {e.old != null && " → "}
              {e.new ?? <span className="text-fg-faint">пусто</span>}
            </span>
          )}
        </Row>
      ))}
    </ul>
  );
}

export function ProductHistoryDialog({ dealId, row, title, onClose }: { dealId: number; row: number; title: string; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>("issue");
  const q = useQuery(productHistoryQuery(dealId, row));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !e.defaultPrevented && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const tabs: [Tab, string][] = [
    ["issue", "Выдача"],
    ["tasks", "Задачи"],
    ["billing", "Биллинг"],
    ["price", "Цена"],
    ["edits", "Правки"],
  ];
  const count = (k: Tab, d: ProductHistory) =>
    k === "tasks" ? d.tasks.length : k === "billing" ? (d.billing?.rows.length ?? 0) : k === "price" ? d.price.length : k === "edits" ? d.edits.length : 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" onClick={onClose}>
      <div
        className="flex max-h-full w-full max-w-[480px] flex-col overflow-hidden rounded-[16px] bg-bg text-[13px] text-fg"
        onClick={(e) => e.stopPropagation()}
        data-annot="история товара"
      >
        <div className="flex flex-none items-center gap-2 border-b border-border px-4 py-2.5">
          <div className="min-w-0 flex-1 text-[15px] font-medium">История · {title}</div>
          <button type="button" onClick={onClose} className="text-fg-muted hover:text-fg" title="Закрыть (Esc)">
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className="flex flex-none flex-wrap gap-1.5 px-4 pt-2.5">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} className={tabBtn(tab === k)}>
              {label}
              {q.data && count(k, q.data) > 0 ? ` ${count(k, q.data)}` : ""}
            </button>
          ))}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2.5">
          {q.isPending ? (
            <div className="text-fg-muted">Загружаю историю из CRM…</div>
          ) : q.isError ? (
            <div className="text-err">Не удалось получить историю: {q.error.message}</div>
          ) : tab === "issue" ? (
            <Issue h={q.data} />
          ) : tab === "tasks" ? (
            <Tasks h={q.data} />
          ) : tab === "billing" ? (
            <Billing h={q.data} />
          ) : tab === "price" ? (
            <Price h={q.data} />
          ) : (
            <Edits h={q.data} />
          )}
        </div>
      </div>
    </div>
  );
}
